import { observer } from "mobx-react-lite";
import { ScaleIcon, SettingsIcon } from "lucide-react";

import ComparisonTypeSelector, { type CompareBlockComparisonType } from "./ComparisonTypeSelector";
import JsonPathInput from "./JsonPathInput";
import NumberOperatorSelector, { type CompareNumberOperator } from "./NumberOperatorSelector";

import { CompareConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { DoubleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { conditional, required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";

const buildComparisonTypeConfig = (
  type: CompareBlockComparisonType,
): NonNullable<CompareConfiguration["comparison_type"]> => {
  if (type === "JSON") {
    return { type: "JSON", json_path: "$.*" };
  }
  if (type === "NUMBER") {
    return { type: "NUMBER", operator: "EQ" };
  }
  return { type };
};

const validationSchema = createBlockSchema<CompareConfiguration>()
  .requireName()
  .addRule(required("configuration.comparison_type.type", "Comparison type is required"))
  .addRule(
    conditional(
      "configuration.comparison_type.json_path",
      payload => payload.configuration?.comparison_type?.type === "JSON",
      required(
        "configuration.comparison_type.json_path",
        "JSON path is required when using JSON comparison",
      ),
    ),
  )
  .requireDoubleSource()
  .build();

const CompareBlockCore = observer(
  ({ sources = createEmptySources() }: Omit<BlockProps<CompareConfiguration>, "DefaultFields">) => {
    const block = useBlock<CompareConfiguration>();
    const isReadonly = useIsReadonly();

    const handleComparisonTypeChange = (type: CompareBlockComparisonType) => {
      block.setConfiguration({
        ...block.configuration,
        comparison_type: buildComparisonTypeConfig(type),
        sources: block.configuration?.sources || ["", ""],
      });
    };

    const handleJsonPathChange = (path: string) => {
      if (block.configuration?.comparison_type?.type === "JSON") {
        block.setConfiguration({
          ...block.configuration,
          comparison_type: {
            type: "JSON",
            json_path: path,
          },
          sources: block.configuration?.sources || ["", ""],
        });
      }
    };

    const handleNumberOperatorChange = (operator: CompareNumberOperator) => {
      if (block.configuration?.comparison_type?.type === "NUMBER") {
        block.setConfiguration({
          ...block.configuration,
          comparison_type: { type: "NUMBER", operator },
          sources: block.configuration?.sources || ["", ""],
        });
      }
    };

    const comparisonType = block.configuration?.comparison_type?.type;
    const jsonPath =
      block.configuration?.comparison_type?.type === "JSON"
        ? block.configuration.comparison_type.json_path
        : null;
    const numberOperator =
      block.configuration?.comparison_type?.type === "NUMBER"
        ? block.configuration.comparison_type.operator
        : undefined;

    return (
      <>
        <SectionGroup
          title="Comparison Configuration"
          description="Configure how values should be compared"
          icon={<SettingsIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <ValidatedField name="Comparison Type" path="configuration.comparison_type.type">
              <ComparisonTypeSelector
                comparisonType={comparisonType}
                onComparisonTypeChange={handleComparisonTypeChange}
                disabled={isReadonly}
              />
            </ValidatedField>

            {comparisonType === "NUMBER" && (
              <ValidatedField name="Operator" path="configuration.comparison_type.operator">
                <NumberOperatorSelector
                  operator={numberOperator}
                  onOperatorChange={handleNumberOperatorChange}
                  disabled={isReadonly}
                />
              </ValidatedField>
            )}

            {comparisonType === "JSON" && (
              <ValidatedField name="JSON Path" path="configuration.comparison_type.json_path">
                <JsonPathInput
                  jsonPath={jsonPath}
                  onJsonPathChange={handleJsonPathChange}
                  disabled={isReadonly}
                />
              </ValidatedField>
            )}
          </div>
        </SectionGroup>

        <SectionGroup
          title="Data Sources"
          description="Select the values to compare"
          icon={<ScaleIcon className="h-4 w-4" />}
        >
          <DoubleSourceSelector
            sources={sources}
            firstName="Source"
            secondName="Target"
            firstValidationPath="configuration.sources.0"
            secondValidationPath="configuration.sources.1"
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<CompareConfiguration>(CompareBlockCore, {
  validationSchema,
});
